import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { ServiceItem, FeatureItem } from '../../types/components';

const JobSeekerServices: FC = () => {
  const services: ServiceItem[] = [
    {
      title: 'Resume Builder',
      description: 'Polished, ATS-friendly resume with expert templates ',
      features: ['ATS friendly', 'One click PDF export', '48 hour delivery period'],
      price: '₹99',
      popular: false
    },
    {
      title: 'Job Finder',
      description: 'AI assisted job matching and dedicated candidates dashboard.',
      features: ['Dedicated Profile Dashboard', 'AI assisted match making', 'Email and Dashboard alerts'],
      price: 'Free',
      popular: true
    }
  ];

  const features: FeatureItem[] = [
    { icon: '', title: 'Smart Matching', description: 'AI-powered matching connects your profile to the right companies.' },
    { icon: '', title: 'Clean Analytics', description: 'Track views, clicks and application progress at a glance.' },
    { icon: '', title: 'Advanced Search', description: 'Search companies by skills, roles and locations instantly.' },
    { icon: '', title: 'Mobile Ready', description: 'Manage your profile and applications on any device.' },
    { icon: '', title: 'Secure Platform', description: 'Privacy-first platform with secure storage.' },
    { icon: '', title: 'Fast Updates', description: 'Profile changes and alerts update in real-time.' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-primary-teaGreen/5 to-white">
      {/* Hero */}
      <section className="relative overflow-hidden pt-20 md:pt-24 pb-16">
        <div className="absolute -top-20 -right-20 h-64 w-64 md:h-96 md:w-96 rounded-full bg-primary-teaGreen/20 blur-3xl animate-pulse" />
        <div className="absolute -bottom-16 -left-16 h-64 w-64 md:h-96 md:w-96 rounded-full bg-primary-lightGreen/15 blur-3xl animate-pulse" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-primary-teaGreen/30 text-primary-black mb-6 border border-primary-teaGreen/40">Services for Job Seekers</span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-primary-black mb-6 leading-tight">
              Find Your <span className="text-primary-lightGreen">Dream Job</span>
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed">
              Build an ATS‑friendly resume and find companies faster with a beautiful, fast, and secure experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="px-8 py-4 text-lg">Get Started</Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" className="px-8 py-4 text-lg">Talk to us</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

     

      {/* Plans */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-black mb-4">Simple pricing for job seekers</h2>
            <p className="text-lg text-gray-600">Resume building and Job discovery plans</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className={`relative bg-white rounded-3xl p-8 border ${service.popular ? 'border-primary-lightGreen ring-2 ring-primary-lightGreen/60' : 'border-gray-100'} shadow-sm hover:shadow-xl transition-all duration-300`}> 
                {service.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-primary-lightGreen text-primary-black px-4 py-1 rounded-full text-sm font-semibold">Most Popular</span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-primary-black mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <div className="text-4xl font-extrabold text-primary-black">{service.price}</div>
                </div>
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className={`w-full ${service.popular ? 'bg-primary-lightGreen text-primary-black hover:bg-primary-teaGreen' : 'bg-gray-900 text-white hover:bg-gray-800'}`}>
                    {service.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

    

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-r from-primary-teaGreen/30 to-primary-lightGreen/20 p-8 md:p-12 lg:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-black mb-4">Ready to Start Your Journey?</h2>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">Join thousands of job seekers who have found their dream jobs through Dhoondh.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="px-8 py-4 text-lg">Get Started Free</Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="px-8 py-4 text-lg border-primary-black text-primary-black hover:bg-primary-black hover:text-white">Contact Support</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobSeekerServices;
